import type { GableRoofExtrusion } from "building-types/roofs/gableRoofExtrusionTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofHeelHeightOffsets } from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { Face } from "geom/brep";
import { planeBSPFromPlane } from "geom/planeBSP";
import { vec3Dot } from "geom/vec3";

export const NULL_HEEL_HEIGHT_OFFSETS = makeRoofHeelHeightOffsets();

export function makeRoofHeelHeightOffsets(
  baseOffset: number = 0,
  leftOffset: number = baseOffset,
  rightOffset: number = baseOffset,
  startOffset: number = baseOffset,
  endOffset: number = baseOffset,
): RoofHeelHeightOffsets {
  const refOffset = Math.min(leftOffset, rightOffset, startOffset, endOffset);

  return {
    refOffset,
    leftOffset: leftOffset - refOffset,
    rightOffset: rightOffset - refOffset,
    startOffset: startOffset - refOffset,
    endOffset: endOffset - refOffset,
  };
}

export function makeRoofHeelHeightOffsetsLegacy(
  baseOffset: number = 0,
  leftOffset: number = baseOffset,
  rightOffset: number = baseOffset,
): RoofHeelHeightOffsets {
  const refOffset = Math.min(leftOffset, rightOffset);

  return {
    refOffset,
    leftOffset: leftOffset - refOffset,
    rightOffset: rightOffset - refOffset,
    startOffset: 0,
    endOffset: 0,
  };
}

export function invertRoofHeelHeightSideOffsets({
  refOffset,
  leftOffset,
  rightOffset,
  startOffset,
  endOffset,
}: RoofHeelHeightOffsets): RoofHeelHeightOffsets {
  return {
    refOffset,
    leftOffset: rightOffset,
    rightOffset: leftOffset,
    startOffset,
    endOffset,
  };
}

export function invertGableRoofType(roofType: GableRoofType): GableRoofType {
  if (roofType === GableRoofType.DEFAULT || roofType === GableRoofType.AUTO_SIDE_SHED_ROOF) {
    return roofType;
  }

  return roofType === GableRoofType.LEFT_SIDE_SHED_ROOF
    ? GableRoofType.RIGHT_SIDE_SHED_ROOF
    : GableRoofType.LEFT_SIDE_SHED_ROOF;
}

export function offsetGableRoofHeelHeightOffsets(
  { refOffset, leftOffset, rightOffset, startOffset, endOffset }: RoofHeelHeightOffsets,
  offset: number,
): RoofHeelHeightOffsets {
  return {
    refOffset: refOffset + offset,
    leftOffset,
    rightOffset,
    startOffset,
    endOffset,
  };
}

export function getAlignedHeelHeightOffset(
  roof: GeneratedRoof | null,
  side: RoofSide,
  referenceRoof: GeneratedRoof | null,
  referenceSide: RoofSide,
): number {
  if (!roof || !referenceRoof) {
    return 0;
  }

  const getRoofFace = (roofExtrusion: GableRoofExtrusion, side: RoofSide): Face | null => {
    const { leftFaces, rightFaces, startHipFaces, endHipFaces } = roofExtrusion;

    let faces: Face[] = [];
    switch (side) {
      case RoofSide.LEFT:
        faces = leftFaces;
        break;
      case RoofSide.RIGHT:
        faces = rightFaces;
        break;
      case RoofSide.START:
        faces = startHipFaces;
        break;
      case RoofSide.END:
        faces = endHipFaces;
        break;
      default:
        break;
    }

    if (faces.length === 0) {
      return null;
    }

    return faces[0];
  };

  const face = getRoofFace(roof.roofVolume.extrusion, side);
  const referenceFace = getRoofFace(referenceRoof.roofVolume.extrusion, referenceSide);

  if (!face || !referenceFace) {
    return 0;
  }

  const { plane } = face;
  const { plane: referencePlane } = referenceFace;

  const planeBSP = planeBSPFromPlane(plane);
  const referencePlaneBSP = planeBSPFromPlane(referencePlane);
  const dist = referencePlaneBSP.dist - planeBSP.dist;

  const { nX, nY, nZ } = planeBSP;
  const dot = vec3Dot(nX, nY, nZ, 0, 0, 1);

  return dist / dot;
}

export function getHeelHeightOffset(
  { startOffset, endOffset, leftOffset, rightOffset, refOffset }: RoofHeelHeightOffsets,
  side: RoofSide,
): number {
  switch (side) {
    case RoofSide.START: {
      return startOffset;
    }
    case RoofSide.END: {
      return endOffset;
    }
    case RoofSide.LEFT: {
      return leftOffset;
    }
    case RoofSide.RIGHT: {
      return rightOffset;
    }
    default: {
      return refOffset;
    }
  }
}
